/**
 * Search Results Page
 * Display search results with filtering and sorting
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Container, Main, Flex } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton } from '../../../components/styled/Button';
import productService from '../services/productService';
import { useCart } from '../contexts/CartContext';
import useShopFeatures from '../hooks/useShopFeatures';
import { getRatingValue, getRatingCount, renderStars, formatPrice } from '../utils/productHelpers';
import './Shop.css';

const Search = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const navigate = useNavigate();
  const { addItem: addToCart } = useCart();
  const features = useShopFeatures();
  
  const searchQuery = searchParams.get('q') || '';

  useEffect(() => {
    if (searchQuery) {
      performSearch();
    } else {
      setLoading(false);
    }
  }, [searchQuery, sortBy]);

  const performSearch = async () => {
    try {
      setLoading(true);
      const response = await productService.searchProducts(searchQuery);
      setProducts(response.items || []);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortProducts = (items) => {
    return [...items].sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });
  };

  const sortedProducts = sortProducts(products);

  if (!searchQuery) {
    return (
      <Container>
        <Main>
          <div className="empty-state">
            <h2>🔍 Search Products</h2>
            <p>Enter a search term to find products</p>
            <SecondaryButton as={Link} to="/shop/catalog">
              Browse All Products
            </SecondaryButton>
          </div>
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      <Main>
        <div className="shop-page search-page">
          <div className="search-header">
            <h1>Search Results for "{searchQuery}"</h1>
            <p className="results-count">
              {loading ? 'Searching...' : `${products.length} product${products.length !== 1 ? 's' : ''} found`}
            </p>
          </div>

          {/* Sort Controls */}
          {!loading && products.length > 0 && (
            <Flex justify="flex-end" style={{ marginBottom: '20px' }}>
              <div className="sort-controls">
                <label>Sort by:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
                  <option value="name">Name</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Highest Rated</option>
                </select>
              </div>
            </Flex>
          )}

          {/* Results */}
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Searching products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <h3>No products found for "{searchQuery}"</h3>
              <p>Try a different search term or browse our catalog</p>
              <Flex gap="12px" justify="center">
                <SecondaryButton as={Link} to="/shop/catalog">
                  Browse Catalog
                </SecondaryButton>
                <PrimaryButton onClick={() => navigate('/shop/search?q=')}>
                  New Search
                </PrimaryButton>
              </Flex>
            </div>
          ) : (
            <div className="product-grid">
              {sortedProducts.map(product => (
                <div 
                  key={product.id} 
                  className="product-card-wrapper" 
                  onClick={() => navigate(`/shop/product/${product.id}`)}
                >
                  <Card className="product-card">
                    <div className="product-image-container">
                      <img src={product.image} alt={product.name} className="product-image" />
                    </div>
                    <CardContent>
                      <h3 className="product-name">{product.name}</h3>
                      {product.rating && (
                        <div className="product-rating">
                          <span className="stars">
                            {renderStars(product.rating)}
                          </span>
                          {getRatingCount(product.rating, product.ratingCount) > 0 && (
                            <span className="rating-count">({getRatingCount(product.rating, product.ratingCount)})</span>
                          )}
                        </div>
                      )}
                      <p className="price">${formatPrice(product.price)}</p>
                      {features.stockIndicators && product.stockQty !== undefined && (
                        <div className={`stock-badge ${product.stockQty > 0 ? 'in-stock' : 'out-of-stock'}`}>
                          {product.stockQty > 0 ? `${product.stockQty} in stock` : 'Out of stock'}
                        </div>
                      )}
                      <PrimaryButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (product.stockQty > 0) {
                            addToCart(product);
                          }
                        }}
                        disabled={product.stockQty === 0}
                        className="add-to-cart-btn"
                      >
                        {product.stockQty > 0 ? 'Add to Cart' : 'Out of Stock'}
                      </PrimaryButton>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </div>
      </Main>
    </Container>
  );
};

export default Search;

